<template>
  <Teleport to="body">
    <div
      class="group pointer-events-none fixed inset-0 z-50 flex items-start justify-start"
      :class="{ 'isVisible pointer-events-auto': isOpen }"
    >
      <!-- Backdrop -->
      <div
        class="fixed inset-0 bg-black/50 opacity-0 transition-opacity duration-300 ease-linear group-[.isVisible]:opacity-100"
        @click="$emit('close')"
      ></div>

      <!-- Panel -->
      <div
        class="relative z-10 h-full w-full -translate-x-full overflow-auto bg-white transition-transform duration-300 ease-linear group-[.isVisible]:translate-x-0 md:w-[480px]"
      >
        <div class="flex items-center justify-between p-14 pb-4 pr-10">
          <h2 class="text-2xl font-medium">{{ $t("pages.profile-shop.filters.title") }}</h2>

          <div class="flex items-center gap-4">
            <IAIButton label="Reset" variant="gray" size="small" @click="resetFilters(FilterResetSource.FilterPanel)" />

            <IAIButton
              variant="link"
              size="medium"
              :icon-src="Icons.Close.src"
              icon-side="start"
              label=""
              @click="closePanel"
            />
          </div>
        </div>

        <div class="px-14">
          <Filters />
        </div>

        <div class="sticky bottom-0 z-50 mt-12 flex h-20 w-full border-t border-gray-200 bg-white px-14 pt-1">
          <IAIButton
            class="m-auto w-full"
            :label="label"
            variant="primary"
            :is-loading="isLoading"
            @click="closePanel"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { IAIButton } from "@/core-ui-vue";
import { Icons } from "@/core-ui-vue";
import Filters from "./Filters.vue";
import {
  FilterResetSource,
  ToggleFilterSource,
  useProfileShopFilters,
} from "@/features/profile-shop/composables/useProfileShopFilters";

defineProps<{
  isOpen: boolean;
  label: string;
  isLoading: boolean;
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { resetFilters, sendFilterSelectedEvent } = useProfileShopFilters();

const closePanel = () => {
  sendFilterSelectedEvent(ToggleFilterSource.FilterPanel);
  emit("close");
};
</script>

<style lang="scss" scoped>
.filter-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: start;
  justify-content: flex-end;
  pointer-events: none; /* Hide overlay when closed */
  visibility: hidden; /* Hide overlay when closed */
}

.filter-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s linear;
}

.filter-panel {
  position: relative;
  z-index: 10;
  height: 100%;
  width: 100%;
  background-color: white;
  overflow: auto;
  transform: translateX(100%);
  transition: all 0.3s linear;
}

@media (min-width: 768px) {
  .filter-panel {
    width: 480px;
  }
}

/* Show states */
.filter-overlay.open {
  pointer-events: auto;
  visibility: visible;
}

.filter-overlay.open .filter-backdrop {
  opacity: 1;
}

.filter-overlay.open .filter-panel {
  transform: translateX(0);
}
</style>
